import type { MobileOutputs } from 'shared/api'
import type { TranslationPaths } from 'shared/lib/i18n'

/** Derived purchase status from `mobile.purchases.MyOrders`. */
export type OrderStatus =
	MobileOutputs['purchases']['MyOrders'][number]['status']

/**
 * Every derived purchase status maps to one label. `satisfies` over the full
 * `OrderStatus` union keeps this exhaustive: dropping or adding a status in the
 * contract breaks the typecheck here instead of silently rendering an
 * undefined key. There is no `cancelled` status — a cancelled lot whose refund
 * is still owed reads `refund_pending`.
 */
export const ORDER_STATUS_KEYS = {
	active: 'status.order.active',
	partially_refunded: 'status.order.partiallyRefunded',
	refunded: 'status.order.refunded',
	refund_pending: 'status.order.refundPending',
} as const satisfies Record<OrderStatus, TranslationPaths>

export type OrderBadgeMode = 'primary' | 'secondary' | 'critical'

/** Badge tone for a purchase status: active is live, a pending refund needs
 * attention, a settled refund is muted. */
export function orderStatusBadgeMode(status: OrderStatus): OrderBadgeMode {
	switch (status) {
		case 'active':
			return 'primary'
		case 'refund_pending':
			return 'critical'
		case 'partially_refunded':
		case 'refunded':
			return 'secondary'
	}
}
